import { TextField, Grid, MenuItem, Card, CardHeader, CardContent, Tooltip, Stack, IconButton } from "@mui/material"
import { forwardRef, useEffect, useImperativeHandle, useMemo } from "react"

import { useFormik } from "formik"
import * as Yup from "yup"
import { useStores } from "stores/root-store"
import { rootStore } from "stores/root-store"
import { isItemBOM } from "utils/app-helper"
import { GlobalData } from "shared/global-data"
import VirtualizedAutocomplete from "components/common/virtualized-autocomplete"
import { Add } from "@mui/icons-material"

const BomInfo = forwardRef(({ bomInfo, onAddNewSKU, newSKU }, ref) => {
  const { BillOfMaterialStore } = useStores()
  const isEditing = Boolean(BillOfMaterialStore.bomInfo)

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: bomInfo,
    validationSchema: Yup.object({
      item: Yup.string().max(25).required("Item is required"),
      quantity_um: Yup.string(100).required("Quantity UM is required"),
    }),
    onSubmit: async values => {
      //console.log(values)
    },
  })

  useImperativeHandle(ref, () => ({
    async getFormDataIfValid() {
      const val = await formik.validateForm()
      await formik.submitForm()

      if (!val || Object.keys(val).length == 0) return formik.values
      return null
    },
  }))

  useEffect(() => {
    if (newSKU) formik.setFieldValue("item", newSKU)
  }, [newSKU])

  const bomProductList = useMemo(() => GlobalData.ProductListSelection.filter(x => isItemBOM(x)), [newSKU])

  return (
    <form onSubmit={formik.handleSubmit}>
      <Card>
        <CardHeader title="BOM Information" />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item md={6} xs={12}>
              {isEditing ? (
                <TextField
                  required
                  fullWidth
                  id="item"
                  name="item"
                  variant="outlined"
                  value={formik.values.item}
                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}
                />
              ) : (
                <Stack direction={"row"} spacing={1}>
                  <VirtualizedAutocomplete
                    id="item"
                    readOnly={isEditing}
                    value={formik.values.item}
                    isOptionEqualToValue={(option, value) => option.id === value}
                    onChange={(event, item) => formik.setFieldValue("item", item.id)}
                    options={bomProductList}
                    renderInput={paramsInner => (
                      <Tooltip placement="top" title="Find existing BOM SKU code">
                        <TextField
                          {...paramsInner}
                          required
                          label="Item"
                          name="item"
                          placeholder="product / SKU code"
                          error={Boolean(formik.touched.item && formik.errors.item)}
                          helperText={formik.touched.item && formik.errors.item}
                        />
                      </Tooltip>
                    )}
                  />
                  <Tooltip placement="top" title="Create a new BOM Sku">
                    <IconButton onClick={onAddNewSKU}>
                      <Add />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                error={Boolean(formik.touched.quantity_um && formik.errors.quantity_um)}
                helperText={formik.touched.quantity_um && formik.errors.quantity_um}
                required
                select={!isEditing}
                label="Quantity UM"
                name="quantity_um"
                id="quantity_um"
                onChange={formik.handleChange}
                value={formik.values.quantity_um}
                variant="outlined"
                InputProps={{
                  readOnly: isEditing,
                }}
              >
                {rootStore.getOptionsByFilter("QUANTITY_UOM").map(option => (
                  <MenuItem id={`quantity_um-${option.value}`} key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                label="Item Description"
                name="item_desc"
                id="item_desc"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.item_desc}
                variant="outlined"
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </form>
  )
})

export default BomInfo
